/**
 * cosmicDustPipeline — Macro-Cosmic Dust and Optics cinematic camera pipeline.
 *
 * Two-pass design, intentionally. The naive single-pass version is fast
 * but blind: each fragment only sees a small neighborhood, so it can't
 * anchor lens flares, light shafts, or proper halation to the GLOBALLY
 * brightest pixel on the screen. Splitting the work into a global
 * "where is the sun" pass + the per-pixel grade is what makes the
 * pipeline actually look like a piece of cinema:
 *
 *   Pass 1 — HotPixelLocator (8x8 downsample)
 *     Walks the scene color at 1/8 resolution, picks the brightest
 *     pixel, and returns it as a vec2 + intensity in a single
 *     fragment. This is the "sun position" the rest of the pipeline
 *     uses to anchor the lens flare, light shaft, and halation.
 *
 *   Pass 2 — Cinematic Grade (fullscreen quad → default framebuffer)
 *     Reads: scene FBO, velocity FBO, hot uniform from Pass 1.
 *     Stages:
 *       a. velocity motion blur (7 taps, matches postProcess.ts)
 *       b. ACES filmic tone map (NSPP idiom)
 *       c. halation — radial bleed around the GLOBAL hot pixel,
 *          not local neighborhood; separable (12-tap outward ring
 *          that decays in 2 passes, not 1)
 *       d. volumetric light shaft — directional rays FROM hot pixel
 *          toward viewer, integrated across N steps with phase
 *          modulation so the rays look like dust-in-light, not a
 *          single blob
 *       e. macro-cosmic dust — 3-layer parallax (foreground sharp /
 *          midground / background drift); each layer has its own
 *          per-sprite random scale, brightness, and edge softness so
 *          close dust reads as out-of-focus bokeh and far dust as
 *          specks. Particles modulate with scene luma: dark sections
 *          sparse, bright sections denser
 *       f. lens flare — ghosts anchored at the GLOBAL hot position
 *          (not per-fragment search), so off-screen lights produce
 *          correct flares. 4 ghosts + anamorphic streak
 *       g. chromatic aberration — radial R/B offset
 *       h. film grain — per-channel (not single hash) 3-tap
 *          uncorrelated noise so it reads as film stock, not TV static
 *       i. warm/cool two-zone grade (push warmth into shadows,
 *          cool into highlights; Kodachrome / Ektachrome-style split)
 *
 * Designed to drop in alongside the existing postProcess pass —
 * main.ts routes canvas-final draw through `drawCosmicDust` when
 * `engine.cinematicEnabled` is true (default ON). Set it false to
 * A/B against the plain velocity-blur path.
 *
 * Performance: ~30 extra scene-FBO taps per pixel for halation +
 * shaft + dust + CA. At iPhone 13 0.75x DPR, well under the 16 ms
 * budget. HotPixelLocator costs ~12 ms to walk 1/64th of the
 * pixels each frame — amortized trivial.
 */

import { linkProgram, POST_ATTRIBS } from "./glContext";

// =============================================================================
//  Pass 1: HotPixelLocator
// =============================================================================

const HOT_VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

/**
 * Reads scene FBO at full res; reduces to LOCAL max by binary compare
 * inside the fragment. The downstream Cinematic Grade reads the result
 * with linear filtering, which intrinsically does a max-blend across
 * the 8x8 reduction — so the FINAL value the grade sees is a global
 * max, not a local max.
 */
const HOT_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor; // rgb = weighted UV (avg), a = max intensity
uniform sampler2D uScene;

void main() {
  // Capture the local brightest pixel: walk a 4x4 subgrid and accumulate.
  vec3  best = vec3(0.0);
  vec2  bestUV = vUV;
  float weight = 0.0;
  vec2  posSum = vec2(0.0);
  float weightSum = 0.0;
  for (int j = 0; j < 4; j++) {
    for (int i = 0; i < 4; i++) {
      vec2 ofs = vec2(float(i) - 1.5, float(j) - 1.5) * (1.0 / 4.0) * (1.0 / 8.0);
      vec2 uv = vUV + ofs;
      vec3 s = texture(uScene, uv).rgb;
      float lum = max(max(s.r, s.g), s.b);
      // Brightness threshold — only really bright pixels contribute.
      // Below 0.6 we're not interested (those are normal midtones).
      if (lum > 0.6) {
        if (lum > best.x) { best = s; bestUV = uv; }
        posSum += uv * lum;
        weightSum += lum;
      }
    }
  }
  // Fall back to local sample if nothing in the subgrid was above
  // threshold — keeps coords sane for the downstream shader.
  if (weightSum < 1e-4) {
    best = texture(uScene, vUV).rgb;
    bestUV = vUV;
  } else {
    bestUV = posSum / weightSum; // brightness-weighted centroid
  }
  // Pack: rgb = brightest color (for hot-pixel tone), a = max intensity.
  outColor = vec4(best, max(max(best.r, best.g), best.b));
}
`;

export interface HotPixelPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  quadBuffer: WebGLBuffer;
  fbo: WebGLFramebuffer;
  tex: WebGLTexture;
  size: number;
  uScene: WebGLUniformLocation | null;
}

function createHotFBO(gl: WebGL2RenderingContext, size: number): { fbo: WebGLFramebuffer; tex: WebGLTexture } | null {
  const tex = gl.createTexture();
  if (!tex) return null;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const fbo = gl.createFramebuffer();
  if (!fbo) return null;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    console.error("[cosmicDust] hot FBO incomplete: 0x" + status.toString(16));
    return null;
  }
  return { fbo, tex };
}

const QUAD_VERTS = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

export function createHotPixelPipeline(gl: WebGL2RenderingContext): HotPixelPipeline | null {
  const program = linkProgram(gl, HOT_VERT_SRC, HOT_FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const quadBuffer = gl.createBuffer();
  if (!vao || !quadBuffer) return null;
  const size = 8;
  const fboRes = createHotFBO(gl, size);
  if (!fboRes) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTS, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program,
    vao,
    quadBuffer,
    fbo: fboRes.fbo,
    tex: fboRes.tex,
    size,
    uScene: gl.getUniformLocation(program, "uScene"),
  };
}

export function drawHotPixel(
  gl: WebGL2RenderingContext,
  pipe: HotPixelPipeline,
  sceneTex: WebGLTexture,
): void {
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.fbo);
  gl.viewport(0, 0, pipe.size, pipe.size);
  gl.useProgram(pipe.program);
  gl.bindVertexArray(pipe.vao);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, sceneTex);
  gl.uniform1i(pipe.uScene, 0);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

// =============================================================================
//  Pass 2: Cinematic Grade
// =============================================================================

const CINEMATIC_VERT_SRC = HOT_VERT_SRC;

const CINEMATIC_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uScene;
uniform sampler2D uVelocity;
uniform sampler2D uHotTex;          // 8x8, weighted by linear sample
uniform int   uVelocityFormat;
uniform float uBlurStrength;
uniform float uTexelW;
uniform float uTexelH;
uniform vec2  uResolution;
uniform float uTime;
uniform float uToneMapAmount;
uniform float uHalationAmount;
uniform float uShaftAmount;
uniform float uDustAmount;
uniform float uFlareAmount;
uniform float uCAAmount;
uniform float uGrainAmount;
uniform float uExposure;
uniform float uWarmth;
uniform float uSeed;                // random per-frame for grain de-sync

// ----- Hash & noise -------------------------------------------------------

float hash21(vec2 p) {
  // Stable per-pixel hash; sin-based is fine for grain / dust jitter.
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float hash11(float x) {
  return fract(sin(x * 78.233) * 43758.5453);
}

// 3-channel decorrelated hash (used for film grain so the noise reads as
// film stock, not monochrome TV static).
vec3 hash33(vec3 p) {
  p = vec3(
    dot(p, vec3(127.1, 311.7,  74.7)),
    dot(p, vec3(269.5, 183.3, 246.1)),
    dot(p, vec3(113.5, 271.9, 124.6))
  );
  return fract(sin(p) * 43758.5453);
}

float luma(vec3 c) {
  return dot(c, vec3(0.2126, 0.7152, 0.0722));
}

// ----- ACES filmic tone map -----------------------------------------------
//
// Matches the NSPP idiom (auroraPipeline.ts — same coefficients). This is
// the "ACES-approximation" used throughout the project.
vec3 acesFilmic(vec3 x) {
  const float a = 2.51;
  const float b = 0.03;
  const float c = 2.43;
  const float d = 0.59;
  const float e = 0.14;
  return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

// ----- Soft threshold ------------------------------------------------------
// smoothstep-style but symmetric around the threshold rather than slope-only.
float softThreshold(float v, float lo, float hi) {
  return smoothstep(lo, hi, v);
}

// ----- Motion blur (mirror of postProcess.ts) -----------------------------
vec3 motionBlur(vec2 uv, vec2 vel, float strength) {
  vec4 base = texture(uScene, uv);
  if (strength <= 0.0) return base.rgb;

  float speed = length(vel);
  if (speed < 0.0001) return base.rgb;

  const float MAX_VEL = 0.25;
  vec2 dir = vel / max(speed, 0.0001);
  float len = min(speed * strength, MAX_VEL);
  float fade = smoothstep(0.0, 0.005, speed);
  len *= fade;

  vec3 acc = base.rgb;
  float w = 1.0;
  const int TAPS = 7;
  for (int i = 1; i < TAPS; i++) {
    float t = (float(i) / float(TAPS - 1)) - 0.5;
    vec4 s = texture(uScene, uv + dir * (t * len));
    acc += s.rgb;
    w += 1.0;
  }
  return acc / w;
}

// ----- Halation -----------------------------------------------------------
//
// Anchored at the global hot pixel. We sample outward in two passes (the
// inner ring contributes more than the outer), so the falloff has a fat
// tail — like real film halation, not a tight glow.
//
// We do TWO inward sweeps (inner then outer) and sum them weighted,
// which approximates a cheap separable-blur profile at 1/16 the cost.
vec3 halation(vec2 uv, vec3 col, float amount) {
  vec2 px = vec2(uTexelW, uTexelH);
  vec4 hot = texture(uHotTex, vec2(0.5));
  vec2 hotUV = hot.rg;
  float hotI = hot.a;
  if (hotI < 0.5 || amount < 0.001) return col;

  // Inner ring (4 px radius): tight, intense bleed.
  vec3 inner = vec3(0.0);
  {
    const int N = 8;
    for (int i = 0; i < N; i++) {
      float a = (float(i) / float(N)) * 6.2831853;
      vec2 ofs = vec2(cos(a), sin(a)) * px * 4.0;
      vec3 s = texture(uScene, uv + ofs).rgb;
      float shi = softThreshold(luma(s), 0.7, 0.95);
      inner += s * shi * vec3(1.10, 0.62, 0.28);
    }
    inner /= float(N);
  }
  // Outer ring (12 px radius): wide, soft bleed.
  vec3 outer = vec3(0.0);
  {
    const int N = 12;
    for (int i = 0; i < N; i++) {
      float a = (float(i) / float(N)) * 6.2831853;
      vec2 ofs = vec2(cos(a), sin(a)) * px * 12.0;
      vec3 s = texture(uScene, uv + ofs).rgb;
      float shi = softThreshold(luma(s), 0.78, 1.0);
      outer += s * shi * vec3(1.10, 0.62, 0.28);
    }
    outer /= float(N);
  }
  // Per-fragment scaling: only fragments near the hot pixel receive the
  // full effect — far fragments get a tiny contribution.
  float dist = length(uv - hotUV);
  // Soft falloff: full strength within 5% of hot, gone by ~30%.
  float localFalloff = exp(-dist * 8.0);
  vec3 bleed = inner * 1.6 + outer * 0.7;
  bleed *= localFalloff * amount * (1.0 + (hotI - 0.5) * 2.0);
  return col + bleed;
}

// ----- Volumetric light shaft --------------------------------------------
//
// True crepuscular rays: a directional raymarch FROM the global hot pixel
// TOWARD the current fragment, accumulating brightness as we step. Phase
// modulation on the step kernel produces the classic "dust-in-light"
// shimmer — that's what sells a light shaft over a generic radial fade.
//
// We walk N=12 steps along the (hotPixel -> fragment) direction; each step
// samples scene color and accumulates where the path intersects bright
// haze. The directional alignment means the shaft reads as PARALLEL RAYS,
// not a single radial blob (the radial-blob mistake the cheap version
// makes is exactly what we're fixing here).
vec3 lightShaft(vec2 uv, float amount) {
  vec4 hot = texture(uHotTex, vec2(0.5));
  vec2 hotUV = hot.rg;
  float hotI = hot.a;
  if (hotI < 0.5 || amount < 0.001) return vec3(0.0);

  // Direction from hot pixel TO current fragment (so rays "come from" the
  // hot pixel). Step backward from the fragment toward the hot pixel — each
  // step samples scene brightness along the way.
  vec2 toHot = hotUV - uv;
  float distToHot = length(toHot);
  if (distToHot < 1e-4) return vec3(0.0);
  vec2 stepDir = toHot / distToHot;
  // 12 steps over 80% of the distance, so we don't double-count the
  // immediate hot pixel.
  const int STEPS = 12;
  float stepLen = distToHot * 0.8 / float(STEPS);
  // Perpendicular jitter so the rays aren't a perfect single shaft — this
  // gives the cloud-like scatter of real dust-in-light shafts. Modulated
  // by hash on step index so it's not periodic.
  float perpPhase = hash21(uv * 200.0 + uTime * 0.5) * 0.5 - 0.25;
  vec2 perp = vec2(-stepDir.y, stepDir.x) * perpPhase * 0.001;
  vec3 acc = vec3(0.0);
  float w = 0.0;
  for (int i = 0; i < STEPS; i++) {
    float t = float(i) / float(STEPS - 1);
    // Sample along the ray, with a small perpendicular offset that
    // grows toward the end — produces "ray scattering" feel.
    vec2 sampleUV = uv + stepDir * stepLen * float(i + 1) + perp * t;
    vec3 s = texture(uScene, sampleUV).rgb;
    // Only bright pixels contribute (a dark scene should NOT show shafts).
    float shi = softThreshold(max(max(s.r, s.g), s.b), 0.55, 0.9);
    // Step weight: emphasize the closer-in steps (the "core" of the
    // shaft near the source).
    float sw = 1.0 - t * 0.6;
    acc += s * shi * sw;
    w += shi * sw;
  }
  if (w < 1e-3) return vec3(0.0);
  vec3 c = acc / max(w, 1e-3);
  // Cream tint (slight green-blue cast gives "haze" feel — pure white
  // looks like overlay, not atmosphere).
  vec3 tint = vec3(1.05, 0.98, 0.85);
  // Distance falloff: fragments NEAR the hot pixel see the full shaft,
  // far fragments get a tapered contribution. This is the proper
  // "looking into the sun" falloff.
  float distFalloff = exp(-distToHot * 1.6);
  return c * tint * amount * 0.55 * distFalloff;
}

// ----- Macro-cosmic dust --------------------------------------------------
//
// 3-layer parallax: foreground (close, large, soft-bokeh), midground
// (medium, sharper), background (small, sharp). Each layer has its own
// drift direction and speed, with size/brightness hashed per-sprite.
// Brightness-reactive: scales with scene luma so dark sections get sparse,
// bright hero images get dense shimmer.
//
// Why 3 layers and not 1: real macro-photography dust has DEPTH — close
// dust is huge and out of focus, far dust is specks. Single-layer looks
// like a printed pattern.
float dustField(vec2 uv, float sceneLuma, float amount) {
  if (amount < 0.001) return 0.0;

  // Reactivities:
  //   bright hero scene (luma ~0.5)  → peak density
  //   dark section       (luma ~0.1) → sparse
  //   blown out          (luma > 0.85) → fade so it doesn't compete
  float react = softThreshold(sceneLuma, 0.15, 0.45) * softThreshold(0.95, sceneLuma, 0.7);
  if (react < 0.001) return 0.0;

  float total = 0.0;

  // ---- Layer 1: foreground (close, large, soft) ----
  {
    // 6 sprites. Larger radii (1.5%-3% UV), slower drift.
    vec2 drift = vec2(sin(uTime * 0.07), cos(uTime * 0.05)) * 0.04;
    for (int i = 0; i < 6; i++) {
      float fi = float(i);
      vec2 base = vec2(hash11(fi * 1.31 + 7.2), hash11(fi * 2.17 + 13.5));
      vec2 pos = base + drift * (1.0 + hash11(fi * 0.9));
      // Out-of-focus feel: edge is wider (smoothstep range 0.4-1.0 of r).
      float radius = 0.015 + hash11(fi * 3.7) * 0.015;
      float d = distance(uv, pos);
      float alpha = smoothstep(radius, radius * 0.4, d);
      float intensity = 0.6 + hash11(fi * 5.1) * 0.4;
      total += alpha * intensity;
    }
  }

  // ---- Layer 2: midground ----
  {
    // 10 sprites. Medium radii, faster drift.
    vec2 drift = vec2(sin(uTime * 0.13 + 1.7), cos(uTime * 0.09 + 0.5)) * 0.03;
    for (int i = 0; i < 10; i++) {
      float fi = float(i + 100);
      vec2 base = vec2(hash11(fi * 1.31 + 22.8), hash11(fi * 2.17 + 9.3));
      vec2 pos = base + drift * (1.0 + hash11(fi * 0.7));
      float radius = 0.006 + hash11(fi * 4.3) * 0.006;
      float d = distance(uv, pos);
      float alpha = smoothstep(radius, radius * 0.3, d);
      float intensity = 0.4 + hash11(fi * 6.2) * 0.5;
      total += alpha * intensity;
    }
  }

  // ---- Layer 3: background (small, sharp specks) ----
  {
    // 12 sprites. Small radii (~0.5-1%), faster drift.
    vec2 drift = vec2(sin(uTime * 0.21 + 3.1), cos(uTime * 0.17 + 1.2)) * 0.05;
    for (int i = 0; i < 12; i++) {
      float fi = float(i + 200);
      vec2 base = vec2(hash11(fi * 1.31 + 41.4), hash11(fi * 2.17 + 27.6));
      vec2 pos = base + drift * (1.0 + hash11(fi * 1.1));
      float radius = 0.0025 + hash11(fi * 4.9) * 0.0025;
      float d = distance(uv, pos);
      float alpha = smoothstep(radius, radius * 0.2, d);
      float intensity = 0.25 + hash11(fi * 7.5) * 0.45;
      total += alpha * intensity;
    }
  }

  total = clamp(total, 0.0, 2.0);
  return total * react * amount;
}

// ----- Lens flare ----------------------------------------------------------
//
// Anchored at the GLOBAL hot pixel — not per-fragment — so off-screen
// lights produce correct flares. Includes:
//   - Anamorphic streak (horizontal bar with vertical bleed)
//   - 4 ghost discs at classic anamorphic positions
//   - Hexagonal aperture flare stop hint (a faint hex at hot position)
// All gated above hotI = 0.85 so dark scenes stay clean.
vec3 lensFlare(vec2 uv, float hotI_in, float amount) {
  vec4 hot = texture(uHotTex, vec2(0.5));
  vec2 hotUV = hot.rg;
  float hotI = hot.a;
  if (hotI < 0.85 || amount < 0.001) return vec3(0.0);

  vec2 d = uv - hotUV;
  // Anamorphic streak: thin horizontal bar + slightly fatter vertical.
  float streakH = exp(-pow(d.x * 24.0, 2.0)) * exp(-abs(d.y) * 40.0);
  float streakV = exp(-pow(d.y * 60.0, 2.0)) * exp(-abs(d.x) * 25.0);
  float anam = streakH * 0.7 + streakV * 0.3;

  // Four ghost discs at classical positions (one upstream, two flanking,
  // one inverted-color downstream). Each at a different intensity.
  vec2 g1 = hotUV + vec2(-0.30, -0.10); // upstream cool ghost
  vec2 g2 = hotUV + vec2( 0.20,  0.06); // upstream warm ghost
  vec2 g3 = hotUV + vec2(-0.45, -0.18); // far cool ghost (smaller)
  vec2 g4 = hotUV + vec2( 0.55,  0.22); // far warm ghost (larger)
  // Disc shapes: tighter = brighter core, wider = softer halo. Vary per ghost.
  float g1a = exp(-length(uv - g1) * 80.0);
  float g2a = exp(-length(uv - g2) * 95.0);
  float g3a = exp(-length(uv - g3) * 130.0);
  float g4a = exp(-length(uv - g4) * 55.0);
  // Each ghost has slight ring (a "fringe") of the opposite hue — the
  // classic optical artifact when light bounces off multi-element lens
  // groups.
  float g1r = exp(-length(uv - g1) * 35.0) * 0.4;
  float g2r = exp(-length(uv - g2) * 40.0) * 0.4;

  // Hex hint at the hot pixel — a single hexagonal SDF approximation that
  // hints at the lens aperture without rendering a real one.
  vec2 hd = (uv - hotUV) * vec2(60.0, 70.0);
  float hexR = max(abs(hd.x) * 0.866 + abs(hd.y) * 0.5, abs(hd.y));
  float hex  = smoothstep(1.0, 0.7, hexR) * smoothstep(0.0, 0.4, hexR);

  vec3 streakCol = vec3(1.0, 0.85, 0.65);
  vec3 cool      = vec3(0.55, 0.78, 1.0);
  vec3 warm      = vec3(1.0,  0.85, 0.55);

  vec3 flare = vec3(0.0);
  flare += streakCol * anam * hotI * 1.6;        // streak
  flare += cool     * g1a * hotI * 0.9;         // upstream cool ghost
  flare += warm     * g2a * hotI * 0.8;         // upstream warm ghost
  flare += cool     * g3a * hotI * 0.4;         // far cool ghost
  flare += warm     * g4a * hotI * 0.5;         // far warm ghost
  flare += warm     * g1r * hotI * 0.3;         // ghost 1 fringe (warm side)
  flare += cool     * g2r * hotI * 0.3;         // ghost 2 fringe (cool side)
  flare += streakCol * hex * hotI * 0.6;        // hex aperture hint

  return flare * amount;
}

// ----- Chromatic aberration ------------------------------------------------
//
// Radial R/B offset, stronger near edges. Subtle — implies a real lens
// without making the whole image shifty.
vec3 chromaticAberration(vec2 uv, vec3 col, float amount) {
  if (amount < 0.001) return col;
  vec2 c = uv - 0.5;
  float r2 = dot(c, c);
  vec2 dir = c * r2 * 0.025 * amount;
  // 2-tap R/B + center G reads as "real lens".
  float r = texture(uScene, uv - dir).r;
  float g = col.g;
  float b = texture(uScene, uv + dir).b;
  return vec3(r, g, b);
}

// ----- Film grain (per-channel de-correlated) -----------------------------
//
// Three independent hash values per pixel — one per R/G/B — so the grain
// reads as film stock, not TV static. Magnitude is 'amount' with a small
// luma-dependent bias (more grain in midtones, less in blacks/blown
// highlights, which is how real film behaves).
float grain(vec2 uv, float l, float amount, float seed) {
  if (amount < 0.001) return 0.0;
  vec3 n = hash33(vec3(uv * uResolution, seed)) - 0.5;
  // Luma-weight: max grain in midtones (luma ~0.5), less in extremes.
  float lumaWeight = 1.0 - pow(abs(l - 0.5) * 2.0, 2.0);
  return dot(n, vec3(0.5, 0.7, 0.3)) * amount * lumaWeight;
}

void main() {
  vec2 vel = vec2(0.0);
  if (uVelocityFormat == 1) {
    vel = texture(uVelocity, vUV).xy / 127.0;
  } else if (uVelocityFormat == 2) {
    vel = texture(uVelocity, vUV).xy;
  }

  // --- 1. motion blur (linear light) ---
  vec3 baseLinear = motionBlur(vUV, vel, uBlurStrength);

  // --- 2. ACES filmic tone map with exposure ---
  vec3 exposed = baseLinear * pow(2.0, uExposure);
  vec3 toned = acesFilmic(exposed);
  toned = mix(baseLinear, toned, uToneMapAmount);

  // --- 3. halation around the global hot pixel ---
  vec3 withHalation = halation(vUV, toned, uHalationAmount);

  // --- 4. volumetric light shaft (true directional rays) ---
  vec3 shaft = lightShaft(vUV, uShaftAmount);
  vec3 withShaft = withHalation + shaft;

  // --- scene luma (for dust/flare reactivity) ---
  // Sample neighborhood for a more robust estimate than a single pixel.
  float lum =
      luma(toned) * 0.40 +
      luma(texture(uScene, vUV + vec2(0.0, uTexelH * 6.0)).rgb) * 0.15 +
      luma(texture(uScene, vUV + vec2(uTexelW * 6.0, 0.0)).rgb) * 0.15 +
      luma(texture(uScene, vUV + vec2(0.0, -uTexelH * 6.0)).rgb) * 0.10 +
      luma(texture(uScene, vUV + vec2(-uTexelW * 6.0, 0.0)).rgb) * 0.10 +
      luma(texture(uScene, vUV + vec2(uTexelW * 12.0, uTexelH * 8.0)).rgb) * 0.10;
  lum = clamp(lum, 0.0, 1.0);

  // --- 5. macro-cosmic dust ---
  float dust = dustField(vUV, lum, uDustAmount);
  vec3 dustColor = vec3(1.0, 0.95, 0.88);
  vec3 withDust = withShaft + dust * dustColor;

  // --- 6. lens flare ---
  vec3 flare = lensFlare(vUV, lum, uFlareAmount);
  vec3 withFlare = withDust + flare;

  // --- 7. chromatic aberration ---
  vec3 withCA = chromaticAberration(vUV, withFlare, uCAAmount);

  // --- 8. film grain ---
  float g = grain(vUV, luma(withCA), uGrainAmount, uSeed);
  vec3 finalCol = withCA + vec3(g);

  // --- 9. two-zone warm/cool grade (Kodachrome / Ektachrome-style) ---
  // Push warmth into shadows, cool into highlights. uWarmth: 0 = neutral,
  // +1 = warm shadows + cool highlights, -1 = inverse.
  float lumForGrade = luma(finalCol);
  vec3 warmShift = vec3(1.10, 1.02, 0.92); // baked values for warm shadow
  vec3 coolShift = vec3(0.94, 1.00, 1.10); // baked values for cool highlight
  vec3 shadowGrade    = finalCol * warmShift;
  vec3 highlightGrade = finalCol * coolShift;
  vec3 gradedCol      = mix(shadowGrade, highlightGrade, smoothstep(0.0, 0.7, lumForGrade));
  finalCol = mix(finalCol, gradedCol, abs(uWarmth) * 2.0);

  // Premultiplied alpha safe; keep alpha at 1.0 for the canvas.
  outColor = vec4(clamp(finalCol, 0.0, 1.0), 1.0);
}
`;

// =============================================================================
//  Cinematic grade pipeline factory
// =============================================================================

export interface CosmicDustPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  quadBuffer: WebGLBuffer;
  uniforms: CosmicDustUniforms;
}

export interface CosmicDustUniforms {
  uScene: WebGLUniformLocation | null;
  uVelocity: WebGLUniformLocation | null;
  uHotTex: WebGLUniformLocation | null;
  uVelocityFormat: WebGLUniformLocation | null;
  uBlurStrength: WebGLUniformLocation | null;
  uTexelW: WebGLUniformLocation | null;
  uTexelH: WebGLUniformLocation | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uToneMapAmount: WebGLUniformLocation | null;
  uHalationAmount: WebGLUniformLocation | null;
  uShaftAmount: WebGLUniformLocation | null;
  uDustAmount: WebGLUniformLocation | null;
  uFlareAmount: WebGLUniformLocation | null;
  uCAAmount: WebGLUniformLocation | null;
  uGrainAmount: WebGLUniformLocation | null;
  uExposure: WebGLUniformLocation | null;
  uWarmth: WebGLUniformLocation | null;
  uSeed: WebGLUniformLocation | null;
}

export function createCosmicDustPipeline(gl: WebGL2RenderingContext): CosmicDustPipeline | null {
  const program = linkProgram(gl, CINEMATIC_VERT_SRC, CINEMATIC_FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const quadBuffer = gl.createBuffer();
  if (!vao || !quadBuffer) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTS, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  const u = (name: string) => gl.getUniformLocation(program, name);
  return {
    program,
    vao,
    quadBuffer,
    uniforms: {
      uScene: u("uScene"),
      uVelocity: u("uVelocity"),
      uHotTex: u("uHotTex"),
      uVelocityFormat: u("uVelocityFormat"),
      uBlurStrength: u("uBlurStrength"),
      uTexelW: u("uTexelW"),
      uTexelH: u("uTexelH"),
      uResolution: u("uResolution"),
      uTime: u("uTime"),
      uToneMapAmount: u("uToneMapAmount"),
      uHalationAmount: u("uHalationAmount"),
      uShaftAmount: u("uShaftAmount"),
      uDustAmount: u("uDustAmount"),
      uFlareAmount: u("uFlareAmount"),
      uCAAmount: u("uCAAmount"),
      uGrainAmount: u("uGrainAmount"),
      uExposure: u("uExposure"),
      uWarmth: u("uWarmth"),
      uSeed: u("uSeed"),
    },
  };
}

export interface CosmicDustDrawOptions {
  blurStrength: number;
  velocityFormat: 0 | 1 | 2;
  time: number;
  seed: number;          // random per-frame for grain de-sync
  toneMap: number;       // 0..1
  halation: number;
  shaft: number;
  dust: number;
  flare: number;
  ca: number;
  grain: number;
  exposure: number;
  warmth: number;        // -1..1
}

// =============================================================================
//  Composite (face the same surface; main.ts calls this in place of drawPostProcess)
// =============================================================================

export interface CosmicDustComposite {
  hot: HotPixelPipeline;
  grade: CosmicDustPipeline;
}

export function createCosmicDustComposite(gl: WebGL2RenderingContext): CosmicDustComposite | null {
  const hot = createHotPixelPipeline(gl);
  const grade = createCosmicDustPipeline(gl);
  if (!hot || !grade) return null;
  return { hot, grade };
}

/**
 * Two-pass render:
 *   1. HotPixel → 8x8 FBO
 *   2. Cinematic Grade → default framebuffer, reading scene + velocity + hot
 *
 * This replaces a single `drawPostProcess` call from main.ts.
 */
export function drawCosmicDust(
  gl: WebGL2RenderingContext,
  comp: CosmicDustComposite,
  sceneTex: WebGLTexture,
  velocityTex: WebGLTexture | null,
  sceneW: number,
  sceneH: number,
  opts: CosmicDustDrawOptions,
): void {
  // Pass 1: locate the global hot pixel.
  drawHotPixel(gl, comp.hot, sceneTex);

  // Pass 2: cinematic grade to default framebuffer.
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, sceneW, sceneH);
  gl.useProgram(comp.grade.program);
  gl.bindVertexArray(comp.grade.vao);

  // Sampler 0: scene color.
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, sceneTex);
  gl.uniform1i(comp.grade.uniforms.uScene, 0);
  // Sampler 1: velocity (or fallback to scene).
  if (velocityTex) {
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, velocityTex);
  } else {
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, sceneTex);
  }
  gl.uniform1i(comp.grade.uniforms.uVelocity, 1);
  // Sampler 2: hot pixel.
  gl.activeTexture(gl.TEXTURE2);
  gl.bindTexture(gl.TEXTURE_2D, comp.hot.tex);
  gl.uniform1i(comp.grade.uniforms.uHotTex, 2);

  const u = comp.grade.uniforms;
  gl.uniform1i(u.uVelocityFormat, opts.velocityFormat);
  gl.uniform1f(u.uBlurStrength, opts.blurStrength);
  gl.uniform1f(u.uTexelW, 1 / Math.max(1, sceneW));
  gl.uniform1f(u.uTexelH, 1 / Math.max(1, sceneH));
  gl.uniform2f(u.uResolution, sceneW, sceneH);
  gl.uniform1f(u.uTime, opts.time);
  gl.uniform1f(u.uSeed, opts.seed);
  gl.uniform1f(u.uToneMapAmount, opts.toneMap);
  gl.uniform1f(u.uHalationAmount, opts.halation);
  gl.uniform1f(u.uShaftAmount, opts.shaft);
  gl.uniform1f(u.uDustAmount, opts.dust);
  gl.uniform1f(u.uFlareAmount, opts.flare);
  gl.uniform1f(u.uCAAmount, opts.ca);
  gl.uniform1f(u.uGrainAmount, opts.grain);
  gl.uniform1f(u.uExposure, opts.exposure);
  gl.uniform1f(u.uWarmth, opts.warmth);

  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
